export type ProductCategory =
  | 'Cyanide'
  | 'Caustic'
  | 'Lime'
  | 'Carbon'
  | 'Acids'
  | 'Reagents'
  | 'Other';

export interface Product {
  id: string;
  name: string;
  category: ProductCategory;
  purity?: string;
  description: string;
  applications: string;
  retailPrice: string;
  bulkPrice: string;
}

export const PRODUCT_CATEGORIES: Array<'All' | ProductCategory> = [
  'All',
  'Cyanide',
  'Caustic',
  'Lime',
  'Carbon',
  'Acids',
  'Reagents',
  'Other',
];

export const PRODUCTS: Product[] = [
  {
    id: 'activated-carbon',
    name: 'Activated Carbon',
    category: 'Carbon',
    description:
      'High-activity carbon for gold recovery circuits and process water treatment.',
    applications: 'CIP, CIL, and filtration systems',
    retailPrice: '$185 / 25kg',
    bulkPrice: '$180 / 25kg',
  },
  {
    id: 'hydrated-lime',
    name: 'Hydrated Lime',
    category: 'Lime',
    description:
      'Consistent calcium hydroxide for pH control and slurry conditioning on site.',
    applications: 'Neutralization, flotation prep, dust control',
    retailPrice: '$12 / 25kg',
    bulkPrice: '$10 / 25kg',
  },
  {
    id: 'caustic-soda-99',
    name: 'Caustic Soda 99%',
    category: 'Caustic',
    purity: '99%',
    description:
      'Industrial-grade sodium hydroxide for leaching support and cleaning circuits.',
    applications: 'Mining process plants and construction washdown',
    retailPrice: '$35 / 25kg',
    bulkPrice: '$30 / 25kg',
  },
  {
    id: 'sodium-cyanide-98',
    name: 'Sodium Cyanide 98%',
    category: 'Cyanide',
    purity: '98%',
    description:
      'High-purity sodium cyanide supplied for controlled gold extraction programs.',
    applications: 'Gold leaching under certified handling protocols',
    retailPrice: '$218 / 50kg',
    bulkPrice: '$200 / 50kg',
  },
  {
    id: 'borax-anhydrous',
    name: 'Borax Anhydrous',
    category: 'Reagents',
    description:
      'Flux-grade borax for smelting and assay workflows in the gold room.',
    applications: 'Assay flux, smelting, glassing',
    retailPrice: '$ / kg',
    bulkPrice: '$ / kg',
  },
  {
    id: 'hydrogen-peroxide',
    name: 'Hydrogen Peroxide',
    category: 'Reagents',
    description:
      'Oxidizing agent for detoxification and process chemistry adjustments.',
    applications: 'Cyanide detox, bleaching, oxidation steps',
    retailPrice: '$ / kg',
    bulkPrice: '$ / kg',
  },
  {
    id: 'sulphuric-acid',
    name: 'Sulphuric Acid',
    category: 'Acids',
    description:
      'Concentrated sulphuric acid for mineral processing and industrial cleaning.',
    applications: 'Leaching, pickling, pH adjustment',
    retailPrice: '$ / kg',
    bulkPrice: '$ / kg',
  },
  {
    id: 'nitric-acid',
    name: 'Nitric Acid',
    category: 'Acids',
    description:
      'Assay and refining nitric acid for laboratory and plant use.',
    applications: 'Assay digestion, refining, metal cleaning',
    retailPrice: '$ / kg',
    bulkPrice: '$ / kg',
  },
  {
    id: 'hydrochloric-acid',
    name: 'Hydrochloric Acid',
    category: 'Acids',
    description:
      'Reliable HCl supply for descaling, digestion, and construction prep work.',
    applications: 'Descaling, ore prep, industrial cleaning',
    retailPrice: '$40 /',
    bulkPrice: '$35 / ',
  },
  {
    id: 'soda-ash-dense',
    name: 'Soda Ash Dense',
    category: 'Caustic',
    description:
      'Dense soda ash for alkalinity control and water treatment circuits.',
    applications: 'pH buffering, flotation, glass and chemical plants',
    retailPrice: '$ / kg',
    bulkPrice: '$ / kg',
  },
  {
    id: 'assay-litharge',
    name: 'Assay Litharge',
    category: 'Reagents',
    description:
      'Lead oxide litharge formulated for fire assay and precious metal recovery.',
    applications: 'Fire assay, cupellation support',
    retailPrice: '$ / kg',
    bulkPrice: '$ / kg',
  },
  {
    id: 'wire-wool',
    name: 'Wire Wool',
    category: 'Other',
    description:
      'Fine wire wool for assay room cleaning and precious metal recovery aid.',
    applications: 'Assay benches, polishing, recovery work',
    retailPrice: '$ / pack',
    bulkPrice: '$ / pack',
  },
  {
    id: 'ferrous-sulphate',
    name: 'Ferrous Sulphate',
    category: 'Reagents',
    description:
      'Iron sulphate reagent for cyanide detoxification and water treatment.',
    applications: 'Detox circuits, coagulants, soil treatment',
    retailPrice: '$ / kg',
    bulkPrice: '$ / kg',
  },
];

export const FOCUS_CATEGORIES = [
  {
    key: 'Cyanide' as const,
    title: 'Cyanide',
    blurb: 'Sodium cyanide grades for gold recovery under strict handling standards.',
  },
  {
    key: 'Caustic' as const,
    title: 'Caustic',
    blurb: 'Caustic soda and dense soda ash for alkalinity and process control.',
  },
  {
    key: 'Lime' as const,
    title: 'Lime',
    blurb: 'Hydrated lime for pH management across mining and construction sites.',
  },
  {
    key: 'Carbon' as const,
    title: 'Carbon',
    blurb: 'Activated carbon selected for recovery circuits and water treatment.',
  },
];
